import { useState, useEffect, useRef } from 'react'
import { db } from '../firebase/config'
import { collection, getDocs, doc, updateDoc, increment, addDoc, query, where, Timestamp } from 'firebase/firestore'
import { Search, Play, Pause, Volume2, VolumeX, Heart, Share2, Eye, Clock, Calendar, User, MessageCircle, Send } from 'lucide-react'

function PodcastsPage({ currentUser }) {
  const [podcasts, setPodcasts] = useState([])
  const [comments, setComments] = useState({})
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedPodcast, setSelectedPodcast] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  const [showShareMenu, setShowShareMenu] = useState(false)
  const [showCommentForm, setShowCommentForm] = useState(false)
  const [commentText, setCommentText] = useState('')
  const [submittingComment, setSubmittingComment] = useState(false)
  const audioRef = useRef(null)

  const itemsPerPage = 10

  useEffect(() => {
    loadPodcasts()
  }, [])

  useEffect(() => {
    if (selectedPodcast) {
      updateMetaTags(selectedPodcast)
      incrementViews(selectedPodcast.id)
      loadComments(selectedPodcast.id)
    } else {
      resetMetaTags()
    }
  }, [selectedPodcast?.id])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const updateTime = () => setCurrentTime(audio.currentTime)
    const updateDuration = () => setDuration(audio.duration)
    const handleEnded = () => {
      setIsPlaying(false)
      setCurrentTime(0)
    }

    audio.addEventListener('timeupdate', updateTime)
    audio.addEventListener('loadedmetadata', updateDuration)
    audio.addEventListener('ended', handleEnded)

    return () => {
      audio.removeEventListener('timeupdate', updateTime)
      audio.removeEventListener('loadedmetadata', updateDuration)
      audio.removeEventListener('ended', handleEnded)
    }
  }, [selectedPodcast])

  const updateMetaTags = (podcast) => {
    document.title = `${podcast.title || 'Podcast'} - Radio 100% Johnny`
    
    updateOrCreateMeta('og:title', podcast.title || 'Podcast - Radio Johnny')
    updateOrCreateMeta('og:description', podcast.description || '')
    updateOrCreateMeta('og:image', podcast.coverImage || podcast.imageUrl || '')
    updateOrCreateMeta('og:url', window.location.href)
    updateOrCreateMeta('og:type', 'music.song')
    
    updateOrCreateMeta('twitter:card', 'summary_large_image')
    updateOrCreateMeta('twitter:title', podcast.title || 'Podcast - Radio Johnny')
    updateOrCreateMeta('twitter:description', podcast.description || '')
    updateOrCreateMeta('twitter:image', podcast.coverImage || podcast.imageUrl || '')
    
    updateOrCreateMeta('description', podcast.description || '')
  }

  const resetMetaTags = () => {
    document.title = 'Radio 100% Johnny'
    updateOrCreateMeta('og:title', 'Radio 100% Johnny')
    updateOrCreateMeta('og:description', 'Écoutez Johnny Hallyday 24h/24')
    updateOrCreateMeta('description', 'Écoutez Johnny Hallyday 24h/24')
  }

  const updateOrCreateMeta = (property, content) => {
    let meta = document.querySelector(`meta[property="${property}"]`)
    if (!meta) {
      meta = document.querySelector(`meta[name="${property}"]`)
    }
    
    if (meta) {
      meta.setAttribute('content', content)
    } else {
      meta = document.createElement('meta')
      if (property.startsWith('og:') || property.startsWith('twitter:')) {
        meta.setAttribute('property', property)
      } else {
        meta.setAttribute('name', property)
      }
      meta.setAttribute('content', content)
      document.head.appendChild(meta)
    }
  }

  const loadPodcasts = async () => {
    setLoading(true)
    
    try {
      const q = query(
        collection(db, '100johnny', 'main', 'podcasts'),
        where('published', '==', true)
      )
      const snapshot = await getDocs(q)
      
      const data = snapshot.docs.map(doc => {
        const docData = doc.data()
        return {
          id: doc.id,
          ...docData
        }
      })
      
      data.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          const dateA = a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt)
          const dateB = b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt)
          return dateB - dateA
        }
        return 0
      })
      
      setPodcasts(data)
      
      // Charger les commentaires pour tous les podcasts
      const allComments = {}
      for (const podcast of data) {
        const commentsQuery = query(
          collection(db, '100johnny', 'main', 'podcast_comments'),
          where('articleId', '==', podcast.id),
          where('approved', '==', true)
        )
        const commentsSnapshot = await getDocs(commentsQuery)
        allComments[podcast.id] = commentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      }
      setComments(allComments)
      
    } catch (error) {
      console.error('Erreur chargement podcasts:', error)
      setPodcasts([])
    } finally {
      setLoading(false)
    }
  }

  const loadComments = async (podcastId) => {
    try {
      const q = query(
        collection(db, '100johnny', 'main', 'podcast_comments'),
        where('articleId', '==', podcastId),
        where('approved', '==', true)
      )
      const snapshot = await getDocs(q)
      
      const commentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      
      commentsData.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          const dateA = a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt)
          const dateB = b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt)
          return dateB - dateA
        }
        return 0
      })
      
      setComments({ ...comments, [podcastId]: commentsData })
      
      if (selectedPodcast && selectedPodcast.id === podcastId) {
        setSelectedPodcast(prev => ({
          ...prev,
          stats: {
            ...prev.stats,
            comments: commentsData.length
          }
        }))
      }
    } catch (error) {
      console.error('Erreur chargement commentaires:', error)
    }
  }

  const incrementViews = async (podcastId) => {
    try {
      await updateDoc(doc(db, '100johnny', 'main', 'podcasts', podcastId), {
        'stats.views': increment(1)
      })
    } catch (error) {
      console.error('Erreur increment views:', error)
    }
  }

  const incrementPlays = async (podcastId) => {
    try {
      await updateDoc(doc(db, '100johnny', 'main', 'podcasts', podcastId), {
        'stats.plays': increment(1)
      })
      
      setSelectedPodcast(prev => ({
        ...prev,
        stats: {
          ...prev.stats,
          plays: (prev.stats?.plays || 0) + 1
        }
      }))
      
    } catch (error) {
      console.error('Erreur increment plays:', error)
    }
  }

  const handleLike = async (podcastId) => {
    try {
      await updateDoc(doc(db, '100johnny', 'main', 'podcasts', podcastId), {
        'stats.likes': increment(1)
      })
      
      setSelectedPodcast(prev => ({
        ...prev,
        stats: {
          ...prev.stats,
          likes: (prev.stats?.likes || 0) + 1
        }
      }))
      
      loadPodcasts()
    } catch (error) {
      console.error('Erreur like:', error)
    }
  }

  const handleShare = async (platform, podcast) => {
    const url = window.location.href
    const title = podcast.title || 'Podcast Radio Johnny'
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`,
      copy: url
    }
    
    if (platform === 'copy') {
      try {
        await navigator.clipboard.writeText(url)
        alert('✅ Lien copié dans le presse-papier !')
      } catch (err) {
        const textArea = document.createElement('textarea')
        textArea.value = url
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        alert('✅ Lien copié !')
      }
    } else {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400')
    }
    
    setShowShareMenu(false)
  }

  const handleSubmitComment = async (e) => {
    e.preventDefault()
    
    if (!currentUser) {
      alert('⚠️ Vous devez être connecté pour commenter')
      return
    }
    
    if (!commentText.trim()) {
      alert('⚠️ Veuillez écrire un commentaire')
      return
    }
    
    setSubmittingComment(true)
    
    try {
      await addDoc(collection(db, '100johnny', 'main', 'podcast_comments'), {
        articleId: selectedPodcast.id,
        authorName: currentUser.displayName || 'Anonyme',
        text: commentText,
        userId: currentUser.uid,
        createdAt: Timestamp.now(),
        approved: false
      })
      
      alert('✅ Votre commentaire a été envoyé ! Il sera visible après validation.')
      setCommentText('')
      setShowCommentForm(false)
      
      await updateDoc(doc(db, '100johnny', 'main', 'podcasts', selectedPodcast.id), {
        'stats.comments': increment(1)
      })
    } catch (error) {
      console.error('Erreur envoi commentaire:', error)
      alert('❌ Erreur lors de l\'envoi du commentaire')
    } finally {
      setSubmittingComment(false)
    }
  }

  const formatCommentText = (text) => {
    if (!text) return ''
    
    let formatted = text
      .replace(/\r\n/g, '<br>')
      .replace(/\n/g, '<br>')
      .replace(/\r/g, '<br>')
    
    if (!formatted.includes('<br>')) {
      formatted = formatted
        .replace(/(🎤|🎸|📅|📍|🏠|🎭|🎵|🎶|🎪|✨|👥|📧)/g, '<br>$1')
        .replace(/^<br>/, '')
    }
    
    return formatted
  }

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      audio.pause()
    } else {
      audio.play()
      if (currentTime === 0) {
        incrementPlays(selectedPodcast.id)
      }
    }
    setIsPlaying(!isPlaying)
  }

  const handleSeek = (e) => {
    const audio = audioRef.current
    if (!audio) return

    const rect = e.currentTarget.getBoundingClientRect()
    const percent = (e.clientX - rect.left) / rect.width
    const newTime = percent * duration
    audio.currentTime = newTime
    setCurrentTime(newTime)
  }

  const handleVolumeChange = (e) => {
    const audio = audioRef.current
    if (!audio) return

    const newVolume = parseFloat(e.target.value)
    audio.volume = newVolume
    setVolume(newVolume)
    setIsMuted(newVolume === 0)
  }

  const toggleMute = () => {
    const audio = audioRef.current
    if (!audio) return

    if (isMuted) {
      audio.volume = volume || 0.5
      setIsMuted(false)
    } else {
      audio.volume = 0
      setIsMuted(true)
    }
  }

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const formatDuration = (durationValue) => {
    if (!durationValue) return '0 min'
    const minutes = typeof durationValue === 'string' ? parseInt(durationValue) : durationValue
    if (isNaN(minutes) || minutes === 0) return '0 min'
    return `${minutes} min`
  }

  const filteredPodcasts = podcasts.filter(podcast => {
    const search = searchTerm.toLowerCase()
    return (
      podcast.title?.toLowerCase().includes(search) ||
      podcast.description?.toLowerCase().includes(search) ||
      podcast.category?.toLowerCase().includes(search) ||
      podcast.presenter?.name?.toLowerCase().includes(search)
    )
  })

  const totalPages = Math.ceil(filteredPodcasts.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const currentPodcasts = filteredPodcasts.slice(startIndex, startIndex + itemsPerPage)

  const formatDate = (date) => {
    if (!date) return 'Date non définie'
    try {
      const d = date.toDate ? date.toDate() : new Date(date)
      return d.toLocaleDateString('fr-FR', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      })
    } catch (e) {
      return 'Date invalide'
    }
  }

  if (selectedPodcast) {
    const stats = selectedPodcast.stats || {}
    const coverImage = selectedPodcast.coverImage || selectedPodcast.imageUrl
    const podcastComments = comments[selectedPodcast.id] || []
    
    return (
      <div className="page active">
        <div className="container">
          <button 
            className="btn btn-secondary" 
            onClick={() => {
              setSelectedPodcast(null)
              setIsPlaying(false)
              setCurrentTime(0)
              setShowCommentForm(false)
              setCommentText('')
              if (audioRef.current) {
                audioRef.current.pause()
              }
            }}
            style={{ marginBottom: '20px' }}
          >
            ← Retour aux podcasts
          </button>

          <div className="glass-card">
            {coverImage && (
              <div style={{ 
                width: '100%', 
                maxWidth: '400px',
                margin: '0 auto 30px',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(0, 217, 255, 0.3)'
              }}>
                <img 
                  src={coverImage} 
                  alt={selectedPodcast.title}
                  style={{ 
                    width: '100%', 
                    height: 'auto',
                    display: 'block'
                  }}
                />
              </div>
            )}

            <div style={{ marginBottom: '15px', textAlign: 'center' }}>
              {selectedPodcast.category && (
                <span style={{
                  display: 'inline-block',
                  padding: '6px 12px',
                  background: 'rgba(255, 215, 0, 0.2)',
                  border: '1px solid rgba(255, 215, 0, 0.4)',
                  borderRadius: '12px',
                  fontSize: '0.85rem',
                  color: '#ffd700',
                  marginRight: '10px',
                  fontWeight: 600
                }}>
                  🎙️ {selectedPodcast.category}
                </span>
              )}
              {selectedPodcast.featured && (
                <span style={{
                  display: 'inline-block',
                  padding: '6px 12px',
                  background: 'rgba(0, 217, 255, 0.2)',
                  border: '1px solid rgba(0, 217, 255, 0.4)',
                  borderRadius: '12px',
                  fontSize: '0.85rem',
                  color: '#00d9ff',
                  fontWeight: 600
                }}>
                  ⭐ À la une
                </span>
              )}
            </div>

            <h1 style={{ 
              fontSize: '2rem', 
              fontWeight: 800, 
              marginBottom: '15px',
              lineHeight: 1.3,
              textAlign: 'center'
            }}>
              {selectedPodcast.title || 'Podcast sans titre'}
            </h1>

            {selectedPodcast.presenter && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontSize: '1.1rem',
                color: 'rgba(255,255,255,0.8)',
                marginBottom: '20px'
              }}>
                <User size={18} />
                <span>{selectedPodcast.presenter.name}</span>
                {selectedPodcast.presenter.role && (
                  <span style={{ 
                    fontSize: '0.9rem', 
                    color: 'rgba(255,255,255,0.5)' 
                  }}>
                    • {selectedPodcast.presenter.role}
                  </span>
                )}
              </div>
            )}

            {/* Player Audio */}
            <div style={{
              padding: '30px',
              background: 'rgba(0, 217, 255, 0.05)',
              borderRadius: '20px',
              marginBottom: '30px'
            }}>
              <audio 
                ref={audioRef}
                src={selectedPodcast.audioUrl}
                preload="metadata"
              />

              <div 
                onClick={handleSeek}
                style={{
                  width: '100%',
                  height: '8px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '4px',
                  marginBottom: '20px',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <div style={{
                  width: `${(currentTime / duration) * 100}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #00d9ff, #ffd700)',
                  borderRadius: '4px',
                  transition: 'width 0.1s'
                }} />
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.9rem',
                color: 'rgba(255,255,255,0.6)',
                marginBottom: '20px'
              }}>
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '30px'
              }}>
                <button
                  onClick={togglePlay}
                  style={{
                    width: '70px',
                    height: '70px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #00d9ff, #ffd700)',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'transform 0.3s',
                    boxShadow: '0 10px 30px rgba(0, 217, 255, 0.4)'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                  onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                >
                  {isPlaying ? (
                    <Pause size={32} color="#000" />
                  ) : (
                    <Play size={32} color="#000" style={{ marginLeft: '4px' }} />
                  )}
                </button>

                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px',
                  minWidth: '150px'
                }}>
                  <button
                    onClick={toggleMute}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '5px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {isMuted ? (
                      <VolumeX size={24} color="rgba(255,255,255,0.6)" />
                    ) : (
                      <Volume2 size={24} color="rgba(255,255,255,0.6)" />
                    )}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    style={{
                      flex: 1,
                      cursor: 'pointer'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Stats */}
            <div style={{
              display: 'flex',
              gap: '20px',
              flexWrap: 'wrap',
              justifyContent: 'center',
              padding: '20px',
              background: 'rgba(0, 217, 255, 0.05)',
              borderRadius: '15px',
              marginBottom: '25px'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                padding: '10px 20px',
                background: 'rgba(0, 217, 255, 0.1)',
                borderRadius: '12px'
              }}>
                <Eye size={18} color="#00d9ff" />
                <span style={{ fontWeight: 600 }}>{stats.views || 0}</span>
                <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>vues</span>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                padding: '10px 20px',
                background: 'rgba(255, 20, 147, 0.1)',
                borderRadius: '12px'
              }}>
                <Heart size={18} color="#ff1493" />
                <span style={{ fontWeight: 600 }}>{stats.likes || 0}</span>
                <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>likes</span>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                padding: '10px 20px',
                background: 'rgba(255, 215, 0, 0.1)',
                borderRadius: '12px'
              }}>
                <Play size={18} color="#ffd700" />
                <span style={{ fontWeight: 600 }}>{stats.plays || 0}</span>
                <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>écoutes</span>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                padding: '10px 20px',
                background: 'rgba(147, 51, 234, 0.1)',
                borderRadius: '12px'
              }}>
                <MessageCircle size={18} color="#9333ea" />
                <span style={{ fontWeight: 600 }}>{podcastComments.length}</span>
                <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>commentaires</span>
              </div>
            </div>

            {/* Boutons d'action */}
            <div style={{
              display: 'flex',
              gap: '10px',
              marginBottom: '30px',
              paddingBottom: '30px',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => handleLike(selectedPodcast.id)}
                className="btn"
                style={{
                  padding: '12px 24px',
                  background: 'rgba(255, 20, 147, 0.2)',
                  border: '1px solid rgba(255, 20, 147, 0.4)',
                  borderRadius: '15px',
                  color: '#ff1493',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Heart size={18} />
                J'aime
              </button>

              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  className="btn"
                  style={{
                    padding: '12px 24px',
                    background: 'rgba(255, 215, 0, 0.2)',
                    border: '1px solid rgba(255, 215, 0, 0.4)',
                    borderRadius: '15px',
                    color: '#ffd700',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Share2 size={18} />
                  Partager
                </button>

                {showShareMenu && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    marginTop: '10px',
                    background: 'rgba(0, 0, 0, 0.95)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    padding: '10px',
                    minWidth: '200px',
                    zIndex: 1000
                  }}>
                    {[
                      { key: 'facebook', icon: '📘', label: 'Facebook' },
                      { key: 'twitter', icon: '🐦', label: 'Twitter' },
                      { key: 'linkedin', icon: '💼', label: 'LinkedIn' },
                      { key: 'telegram', icon: '✈️', label: 'Telegram' },
                      { key: 'whatsapp', icon: '💬', label: 'WhatsApp' },
                      { key: 'copy', icon: '📋', label: 'Copier le lien' }
                    ].map((platform) => (
                      <button
                        key={platform.key}
                        onClick={() => handleShare(platform.key, selectedPodcast)}
                        style={{
                          width: '100%',
                          padding: '10px',
                          background: 'transparent',
                          border: 'none',
                          color: '#fff',
                          textAlign: 'left',
                          cursor: 'pointer',
                          borderRadius: '8px',
                          marginBottom: '5px'
                        }}
                        onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                      >
                        {platform.icon} {platform.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div style={{
              fontSize: '1.1rem',
              lineHeight: 1.8,
              color: 'rgba(255,255,255,0.9)',
              marginBottom: '20px'
            }}>
              {selectedPodcast.description}
            </div>

            {/* Infos additionnelles */}
            <div style={{
              display: 'flex',
              gap: '20px',
              flexWrap: 'wrap',
              fontSize: '0.9rem',
              color: 'rgba(255,255,255,0.6)',
              paddingTop: '20px',
              paddingBottom: '20px',
              borderTop: '1px solid rgba(255,255,255,0.1)',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              marginBottom: '40px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Calendar size={16} />
                {formatDate(selectedPodcast.createdAt)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Clock size={16} />
                {formatDuration(selectedPodcast.duration)}
              </div>
            </div>

            {/* Section Commentaires */}
            <div style={{
              marginTop: '50px',
              paddingTop: '40px',
              borderTop: '2px solid rgba(255,255,255,0.1)'
            }}>
              <h2 style={{
                fontSize: '1.8rem',
                fontWeight: 700,
                marginBottom: '25px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <MessageCircle size={28} color="#9333ea" />
                Commentaires ({podcastComments.length})
              </h2>

              {currentUser && (
                <div style={{ marginBottom: '30px' }}>
                  {!showCommentForm ? (
                    <button
                      onClick={() => setShowCommentForm(true)}
                      className="btn btn-primary"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <MessageCircle size={18} />
                      Laisser un commentaire
                    </button>
                  ) : (
                    <form onSubmit={handleSubmitComment} style={{
                      padding: '20px',
                      background: 'rgba(147, 51, 234, 0.05)',
                      borderRadius: '15px',
                      border: '1px solid rgba(147, 51, 234, 0.2)'
                    }}>
                      <textarea
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder="Partagez votre avis..."
                        required
                        rows={4}
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '12px',
                          color: '#fff',
                          fontSize: '1rem',
                          outline: 'none',
                          resize: 'vertical',
                          fontFamily: 'inherit',
                          marginBottom: '15px'
                        }}
                      />
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          type="submit"
                          disabled={submittingComment}
                          className="btn btn-primary"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            opacity: submittingComment ? 0.6 : 1
                          }}
                        >
                          <Send size={18} />
                          {submittingComment ? 'Envoi...' : 'Envoyer'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowCommentForm(false)
                            setCommentText('')
                          }}
                          className="btn btn-secondary"
                        >
                          Annuler
                        </button>
                      </div>
                      <p style={{ 
                        marginTop: '10px', 
                        fontSize: '0.85rem', 
                        color: 'rgba(255,255,255,0.5)'
                      }}>
                        ⓘ Votre commentaire sera visible après validation
                      </p>
                    </form>
                  )}
                </div>
              )}

              {podcastComments.length === 0 ? (
                <p style={{ 
                  textAlign: 'center', 
                  color: 'rgba(255,255,255,0.5)',
                  padding: '40px',
                  fontSize: '1.1rem'
                }}>
                  Aucun commentaire pour le moment. Soyez le premier à commenter !
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {podcastComments.map((comment) => (
                    <div
                      key={comment.id}
                      style={{
                        padding: '20px',
                        background: 'rgba(147, 51, 234, 0.05)',
                        borderRadius: '15px',
                        border: '1px solid rgba(147, 51, 234, 0.2)'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #9333ea, #ec4899)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: '1.2rem'
                          }}>
                            {comment.authorName?.charAt(0).toUpperCase() || 'A'}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '1rem' }}>
                              {comment.authorName || 'Anonyme'}
                            </div>
                            <div style={{ 
                              fontSize: '0.85rem', 
                              color: 'rgba(255,255,255,0.5)' 
                            }}>
                              {formatDate(comment.createdAt)}
                            </div>
                          </div>
                        </div>
                      </div>
                      <p 
                        style={{
                          fontSize: '1rem',
                          lineHeight: 1.6,
                          color: 'rgba(255,255,255,0.9)'
                        }}
                        dangerouslySetInnerHTML={{ 
                          __html: formatCommentText(comment.text)
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="dock-spacer"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="page active">
      <div className="container">
        <div className="page-header">
          <div className="page-badge">🎙️ PODCASTS</div>
          <h1 className="page-title">Podcasts</h1>
          <p className="page-subtitle">Émissions et contenus audio exclusifs</p>
        </div>

        <div className="glass-card" style={{ marginBottom: '20px', padding: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Search size={20} color="rgba(255,255,255,0.5)" />
            <input
              type="text"
              placeholder="Rechercher un podcast..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setCurrentPage(1)
              }}
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                color: '#fff',
                outline: 'none',
                fontSize: '1rem'
              }}
            />
          </div>
        </div>

        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Chargement...</p>
          </div>
        ) : currentPodcasts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🎙️</div>
            <p>{searchTerm ? 'Aucun podcast trouvé' : 'Aucun podcast pour le moment'}</p>
          </div>
        ) : (
          <>
            <div className="content-list">
              {currentPodcasts.map((podcast) => {
                const stats = podcast.stats || {}
                const coverImage = podcast.coverImage || podcast.imageUrl
                
                return (
                  <div 
                    key={podcast.id} 
                    className="content-card"
                    onClick={() => setSelectedPodcast(podcast)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0', overflow: 'hidden' }}>
  {coverImage && (
    <div style={{ position: 'relative', width: '100%', height: '200px' }}>
      <img 
        src={coverImage} 
        alt={podcast.title}
        style={{ 
          width: '100%', 
          height: '100%', 
          objectFit: 'cover',
          borderRadius: '12px 12px 0 0'
        }}
      />
      <div style={{
        position: 'absolute',
        bottom: '8px',
        right: '8px',
        background: 'rgba(0, 0, 0, 0.8)',
        padding: '4px 8px',
        borderRadius: '8px',
        fontSize: '0.75rem',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        <Clock size={12} />
        {formatDuration(podcast.duration)}
      </div>
    </div>
  )}
  
  <div style={{ padding: '15px', flex: 1 }}>
                        <div style={{ marginBottom: '8px' }}>
                          {podcast.category && (
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 10px',
                              background: 'rgba(255, 215, 0, 0.15)',
                              border: '1px solid rgba(255, 215, 0, 0.3)',
                              borderRadius: '10px',
                              fontSize: '0.75rem',
                              color: '#ffd700',
                              marginRight: '8px',
                              fontWeight: 600
                            }}>
                              {podcast.category}
                            </span>
                          )}
                          {podcast.featured && (
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 10px',
                              background: 'rgba(0, 217, 255, 0.15)',
                              border: '1px solid rgba(0, 217, 255, 0.3)',
                              borderRadius: '10px',
                              fontSize: '0.75rem',
                              color: '#00d9ff',
                              fontWeight: 600
                            }}>
                              ⭐ À la une
                            </span>
                          )}
                        </div>

                        <h3 style={{ 
                          fontSize: '1.3rem', 
                          fontWeight: 700, 
                          marginBottom: '10px',
                          lineHeight: 1.3
                        }}>
                          {podcast.title || 'Podcast sans titre'}
                        </h3>

                        <p style={{ 
                          color: 'rgba(255,255,255,0.7)', 
                          marginBottom: '15px',
                          lineHeight: 1.6,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {podcast.description}
                        </p>

                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          gap: '15px',
                          flexWrap: 'wrap',
                          fontSize: '0.85rem',
                          color: 'rgba(255,255,255,0.5)'
                        }}>
                          {podcast.presenter && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              🎤 {podcast.presenter.name}
                            </div>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Calendar size={14} />
                            {formatDate(podcast.createdAt)}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Eye size={14} />
                            {stats.views || 0}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Heart size={14} />
                            {stats.likes || 0}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Play size={14} />
                            {stats.plays || 0}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <MessageCircle size={14} />
                            {comments[podcast.id]?.length || stats.comments || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {totalPages > 1 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '10px',
                marginTop: '30px',
                flexWrap: 'wrap'
              }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
                >
                  ← Précédent
                </button>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  padding: '0 15px'
                }}>
                  <span style={{ color: 'rgba(255,255,255,0.7)' }}>
                    Page {currentPage} sur {totalPages}
                  </span>
                </div>
                <button
                  className="btn btn-secondary"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
                >
                  Suivant →
                </button>
              </div>
            )}

            <div style={{ 
              textAlign: 'center', 
              marginTop: '15px',
              color: 'rgba(255,255,255,0.5)',
              fontSize: '0.9rem'
            }}>
              {filteredPodcasts.length} podcast{filteredPodcasts.length > 1 ? 's' : ''} trouvé{filteredPodcasts.length > 1 ? 's' : ''}
            </div>
          </>
        )}

      </div>
    </div>
  )
}

export default PodcastsPage